// Inline SVG: interface icons, and chain and token marks (drawn here, nothing fetched).

import type { ReactNode, SVGProps } from "react";
import type { ChainKey } from "../chains";

type P = SVGProps<SVGSVGElement> & { size?: number };
const base = ({ size = 18, ...p }: P) => ({ width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true, ...p });

export const IconChevron = (p: P) => <svg {...base(p)}><path d="m6 9 6 6 6-6" /></svg>;
export const IconArrowDown = (p: P) => <svg {...base(p)}><path d="M12 5v14M19 12l-7 7-7-7" /></svg>;
export const IconSwapV = (p: P) => <svg {...base(p)}><path d="M7 4v16M3 16l4 4 4-4M17 20V4M21 8l-4-4-4 4" /></svg>;
export const IconGear = (p: P) => (
  <svg {...base(p)}>
    <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="10" cy="17" r="2" />
  </svg>
);
export const IconClose = (p: P) => <svg {...base(p)}><path d="M18 6 6 18M6 6l12 12" /></svg>;
export const IconSearch = (p: P) => <svg {...base(p)}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>;
export const IconCheck = (p: P) => <svg {...base(p)}><path d="M20 6 9 17l-5-5" /></svg>;
export const IconExternal = (p: P) => <svg {...base(p)}><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>;
export const IconCopy = (p: P) => <svg {...base(p)}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a1 1 0 0 1 1-1h10" /></svg>;
export const IconWallet = (p: P) => <svg {...base(p)}><path d="M19 7V5a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4M21 17v2a1 1 0 0 1-1 1H5a2 2 0 0 1-2-2V6" /><path d="M17 12h4v5h-4a2.5 2.5 0 0 1 0-5Z" /></svg>;
export const IconSun = (p: P) => <svg {...base(p)}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>;
export const IconMoon = (p: P) => <svg {...base(p)}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" /></svg>;
export const IconMonitor = (p: P) => <svg {...base(p)}><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></svg>;
export const IconClock = (p: P) => <svg {...base(p)}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
export const IconAlert = (p: P) => <svg {...base(p)}><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>;
export const IconInfo = (p: P) => <svg {...base(p)}><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></svg>;
export const IconUndo = (p: P) => <svg {...base(p)}><path d="M9 14 4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-3" /></svg>;
export const IconRoute = (p: P) => <svg {...base(p)}><circle cx="6" cy="19" r="2" /><circle cx="18" cy="5" r="2" /><path d="M8 19h7.5a3.5 3.5 0 0 0 0-7h-7a3.5 3.5 0 0 1 0-7H16" /></svg>;
export const IconKey = (p: P) => <svg {...base(p)}><circle cx="7.5" cy="15.5" r="4.5" /><path d="m10.7 12.3 9.3-9.3M17 6l3 3M14 9l2 2" /></svg>;
export const Spinner = ({ size = 18 }: { size?: number }) => (
  <svg className="spinner" width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

// ---- chain and token marks ------------------------------------------------------------------

const Disc = ({ size, fill, children, title }: { size: number; fill: string; children?: ReactNode; title?: string }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" role={title ? "img" : undefined} aria-label={title} aria-hidden={title ? undefined : true} className="logo">
    <circle cx="16" cy="16" r="16" fill={fill} />
    {children}
  </svg>
);
const Glyph = ({ text, color = "#fff", fs = 15 }: { text: string; color?: string; fs?: number }) => (
  <text x="16" y="16" dy="0.35em" textAnchor="middle" fontSize={fs} fontWeight="700" fill={color} fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif">
    {text}
  </text>
);

const ethDiamond = (
  <g fill="#fff">
    <path d="M16 4.5 9 16.2l7 4.1 7-4.1z" fillOpacity="0.95" />
    <path d="m9 17.6 7 9.9 7-9.9-7 4.1z" fillOpacity="0.8" />
  </g>
);
const solBars = (id: string) => (
  <>
    <defs>
      <linearGradient id={id} x1="6" y1="24" x2="26" y2="8" gradientUnits="userSpaceOnUse">
        <stop stopColor="#9945FF" />
        <stop offset="1" stopColor="#14F195" />
      </linearGradient>
    </defs>
    <g fill={`url(#${id})`}>
      <path d="M10.4 20.6h13.2l-2.4 2.6H8z" />
      <path d="M10.4 8.8h13.2l-2.4 2.6H8z" />
      <path d="M21.2 14.7H8l2.4 2.6h13.2z" />
    </g>
  </>
);
const suiDrop = <path fill="#fff" d="M16 6.5c-.3 0-.6.2-.8.4-2.4 3-5.7 6.9-5.7 10.6a6.5 6.5 0 0 0 13 0c0-3.7-3.3-7.6-5.7-10.6a1 1 0 0 0-.8-.4Zm0 15.8a4.8 4.8 0 0 1-4.8-4.8c0-.4 0-.8.2-1.1a7 7 0 0 0 9.2 3.2 4.8 4.8 0 0 1-4.6 2.7Z" />;
const bnbMark = (
  <g fill="#fff">
    <path d="m16 8.4 2.3 2.3-5.6 5.6-2.3-2.3zM19.6 12l2.3 2.3-8.2 8.2-2.3-2.3z" transform="translate(0 -.5)" />
    <path d="m12.4 12-2.3 2.3 2.3 2.3 2.3-2.3zM8.8 15.6 6.5 17.9l2.3 2.3 2.3-2.3z" transform="translate(1.6 -1.6)" />
  </g>
);
const jupRings = (id: string) => (
  <>
    <defs>
      <linearGradient id={id} x1="4" y1="4" x2="28" y2="28" gradientUnits="userSpaceOnUse">
        <stop stopColor="#C7F284" />
        <stop offset="1" stopColor="#00BEF0" />
      </linearGradient>
    </defs>
    <circle cx="16" cy="16" r="16" fill="#0E1621" />
    <g fill="none" stroke={`url(#${id})`} strokeWidth="2.2" strokeLinecap="round">
      <path d="M7 19.5c4-1.2 10.5-5 14.5-9.6" />
      <path d="M8.5 23.2c5-1.6 11.4-6 15-10.8" />
      <path d="M6.6 15.2c3.2-.9 8.5-4.2 11.4-7.6" />
    </g>
  </>
);

/** A chain's mark. */
export function ChainLogo({ chain, size = 24 }: { chain: ChainKey; size?: number }) {
  switch (chain) {
    case "base":
      return (
        <Disc size={size} fill="#0052FF" title="Base">
          <rect x="9.5" y="9.5" width="13" height="13" rx="2.2" fill="#fff" />
        </Disc>
      );
    case "arbitrum":
      return (
        <Disc size={size} fill="#213147" title="Arbitrum">
          <path d="M16 6 7.5 11v10L16 26l8.5-5V11z" fill="none" stroke="#9DCCED" strokeWidth="1.6" />
          <path d="m14.8 10.5-5 13.2 2 1.2 5-13.2zM17.6 13.6l3.8 10.1 2-1.2-3.6-9.5z" fill="#12AAFF" />
          <path d="m16.9 10.5 1.7 4.4" stroke="#fff" strokeWidth="1.8" />
        </Disc>
      );
    case "ethereum":
      return <Disc size={size} fill="#627EEA" title="Ethereum">{ethDiamond}</Disc>;
    case "bsc":
      return <Disc size={size} fill="#F0B90B" title="BNB Chain">{bnbMark}</Disc>;
    case "robinhood":
      return (
        <Disc size={size} fill="#CCFF00" title="Robinhood Chain">
          <path d="M20.8 7.5c-4.9.6-8.8 4-10 8.6l-1.6 6.6 1.7.5 1-4.2c2.5-.1 5-1.5 6.4-3.8l-3.7.4 4.7-2.1c.8-1.8 1.3-3.8 1.5-6Z" fill="#111" />
        </Disc>
      );
    case "hyperevm":
      return (
        <Disc size={size} fill="#0F1A1F" title="HyperEVM">
          <path d="M8 16c1.8-4.6 4.4-4.6 6.2-1.6 1.6 2.6 2.8 2.6 3.6 0 1.8-4.6 4.4-4.6 6.2 1.6-1.8 4.6-4.4 4.6-6.2 1.6-1.6-2.6-2.8-2.6-3.6 0-1.8 4.6-4.4 4.6-6.2-1.6Z" fill="#97FCE4" />
        </Disc>
      );
    case "solana":
      return <Disc size={size} fill="#000" title="Solana">{solBars(`sol-${size}`)}</Disc>;
    case "sui":
      return <Disc size={size} fill="#4DA2FF" title="Sui">{suiDrop}</Disc>;
    case "jupnet":
      return <Disc size={size} fill="#0E1621" title="Jupnet">{jupRings(`jup-${size}`)}</Disc>;
  }
}

const hue = (s: string) => [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);

/** A token's mark by symbol (a fallback disc with its initials for the rest). */
export function TokenLogo({ sym, size = 28 }: { sym: string; size?: number }) {
  switch (sym.toUpperCase()) {
    case "USDC":
      return (
        <Disc size={size} fill="#2775CA" title="USDC">
          <circle cx="16" cy="16" r="10.5" fill="none" stroke="#fff" strokeWidth="1.6" strokeDasharray="20 6" strokeDashoffset="13" />
          <Glyph text="$" fs={14} />
        </Disc>
      );
    case "USDT":
      return (
        <Disc size={size} fill="#26A17B" title="USDT">
          <path fill="#fff" d="M9 9h14v3.2h-5.3v2.1c3.4.2 6 .9 6 1.8s-2.6 1.6-6 1.8V24h-3.4v-6.1c-3.4-.2-6-.9-6-1.8s2.6-1.6 6-1.8v-2.1H9Zm5.3 6.4c-2.4.1-4.2.5-4.2.8s1.8.7 4.2.8v-.2l.1.3c.5 0 1 .1 1.6.1s1.1 0 1.6-.1v-1.7c-.5 0-1-.1-1.6-.1s-1.1 0-1.7.1Zm3.4 0v1.6c2.4-.1 4.2-.5 4.2-.8s-1.8-.7-4.2-.8Z" />
        </Disc>
      );
    case "USDG":
      return (
        <Disc size={size} fill="#0B0F19" title="USDG">
          <circle cx="16" cy="16" r="10" fill="none" stroke="#5EEAD4" strokeWidth="2" />
          <Glyph text="G" fs={12} color="#5EEAD4" />
        </Disc>
      );
    case "USD":
    case "STABLE":
      return (
        <Disc size={size} fill="#16A34A" title={sym}>
          <Glyph text="$" fs={16} />
        </Disc>
      );
    case "ETH":
      return <Disc size={size} fill="#627EEA" title="ETH">{ethDiamond}</Disc>;
    case "WETH":
      return (
        <Disc size={size} fill="#EC4899" title="WETH">
          <circle cx="16" cy="16" r="13.5" fill="#627EEA" />
          {ethDiamond}
        </Disc>
      );
    case "CBBTC":
    case "BTC":
    case "WBTC":
      return (
        <Disc size={size} fill="#F7931A" title={sym}>
          <Glyph text="₿" fs={17} />
        </Disc>
      );
    case "SOL":
      return <Disc size={size} fill="#000" title="SOL">{solBars(`soltk-${size}`)}</Disc>;
    case "BNB":
    case "WBNB":
      return <Disc size={size} fill="#F0B90B" title={sym}>{bnbMark}</Disc>;
    case "SUI":
      return <Disc size={size} fill="#4DA2FF" title="SUI">{suiDrop}</Disc>;
    case "ARB":
      return <ChainLogo chain="arbitrum" size={size} />;
    case "JUP":
      return <Disc size={size} fill="#0E1621" title="JUP">{jupRings(`juptk-${size}`)}</Disc>;
    case "AERO":
      return (
        <Disc size={size} fill="#0433FF" title="AERO">
          <path d="M8 21 16 8l8 13h-4.5L16 15l-3.5 6Z" fill="#fff" />
        </Disc>
      );
    case "BONK":
      return (
        <Disc size={size} fill="#F8A100" title="BONK">
          <Glyph text="B" fs={15} />
        </Disc>
      );
    default:
      return (
        <Disc size={size} fill={`hsl(${hue(sym)} 55% 45%)`} title={sym}>
          <Glyph text={sym.slice(0, 2).toUpperCase()} fs={11} />
        </Disc>
      );
  }
}

/** A token's mark with its chain's in the corner. */
export function TokenOnChain({ sym, chain, size = 32 }: { sym: string; chain: ChainKey; size?: number }) {
  return (
    <span className="token-on-chain" style={{ width: size, height: size }}>
      <TokenLogo sym={sym} size={size} />
      <span className="chain-badge">
        <ChainLogo chain={chain} size={Math.round(size * 0.46)} />
      </span>
    </span>
  );
}
